/*
 * AccountHealthBanner (Entry 184) — a persistent, app-wide alert shown when
 * one of the current user's connected accounts (Google Calendar / Gmail /
 * QuickBooks) has a broken token, with a one-click Reconnect that deep-links
 * to Settings → Connections.
 *
 * It is NOT a dismissible toast: it is a pure projection of server state. It
 * exists exactly as long as GET /api/account-health reports a broken account,
 * so it survives navigation + reload and AUTO-CLEARS once the account is
 * reconnected (the backend nulls the marker on a successful re-auth).
 *
 * Mounted globally from a non-frozen provider (the GlobalTimeTracker / GlobalDialer
 * precedent — createPortal to document.body, no frozen AppShell edit). Self-gates
 * on user, so mounting it unconditionally is safe.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../contexts/AuthContext';
import { accountHealthApi, type NeedsReconnectItem } from '../../services/accountHealthApi';

const POLL_MS = 60_000;
const PAPRIKA = 'var(--paprika)';

const AccountHealthBanner: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<NeedsReconnectItem[]>([]);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const res = await accountHealthApi.get();
      if (mounted.current && res?.success) setItems(res.data.needsReconnect || []);
    } catch {
      /* silent — a failed health check should never itself surface an error */
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!user) { setItems([]); return; }
    refresh();
    const interval = window.setInterval(refresh, POLL_MS);
    // Re-check when the user returns to the tab (e.g. back from the OAuth
    // round-trip) so the banner clears promptly after a reconnect.
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    return () => {
      mounted.current = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', refresh);
    };
  }, [user, refresh]);

  if (!user || items.length === 0) return null;

  const multiple = items.length > 1;
  const primary = items[0];
  const message = multiple
    ? `${items.length} connected accounts need reconnecting`
    : `Your ${primary.label} disconnected${primary.detail ? ` (${primary.detail})` : ''}`;
  const actionLabel = multiple ? 'Review connections' : 'Reconnect';
  const target = multiple ? '/settings?tab=connections' : primary.reconnectPath;

  return createPortal(
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        // Sits above the bottom-right dialer (zIndex 1400) so an overlap stays
        // readable/clickable, but below the incoming-call banner (1500) so a
        // ringing call still wins. Bottom-center clears the top time-tracker
        // pill + the bottom-right toasts.
        bottom: 'calc(var(--safe-area-bottom, 0px) + 16px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1450,
        width: 'min(620px, calc(100vw - 24px))',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: '6px',
        bgcolor: 'var(--paprika-soft)',
        border: `1px solid ${PAPRIKA}`,
        color: 'var(--ink)',
        boxShadow: 'var(--pop)',
        '@keyframes ahbUp': { from: { opacity: 0, transform: 'translate(-50%, 8px)' }, to: { opacity: 1, transform: 'translate(-50%, 0)' } },
        animation: 'ahbUp 240ms ease',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <WarningAmberIcon sx={{ color: PAPRIKA, fontSize: 22, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.3, fontFamily: '"Inter Tight", Inter, sans-serif' }}>
          {message}
        </Typography>
        <Typography sx={{ fontSize: 12.5, opacity: 0.75, lineHeight: 1.3 }}>
          {multiple
            ? 'Some integrations stopped working until you re-authorize them.'
            : 'It stopped syncing until you re-authorize it. Bookings and email can be affected.'}
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="small"
        startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate(target)}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 12.5,
          borderRadius: '4px',
          boxShadow: 'none',
          bgcolor: 'var(--indigo)',
          '&:hover': { bgcolor: 'var(--indigo)', filter: 'brightness(1.08)', boxShadow: 'none' },
        }}
      >
        {actionLabel}
      </Button>
    </Box>,
    document.body,
  );
};

export default AccountHealthBanner;
